import {
  TAG_MAX_LENGTH, codePointLength, hasForbiddenCharacter, hasVisibleCharacter, normalizeTag, tagProblem,
} from "../hqCustomerRules";

/**
 * The config each automation action needs, mirrored from the backend's one rule
 * (`automation_runner._ACTION_CONFIG_FIELDS`, hq-customers-contract.md 4.3). Rule create and
 * update refuse a rule that breaks it with `400 invalid_action_config {index, type, field}`, so
 * the rule forms collect these fields and refuse a value here before sending it. The numbers
 * carry the backend constants' names; a backend test reads this file and pins them.
 *
 * Lengths are code points on the string as sent (no trimming), as the server counts them.
 */
export const TASK_TITLE_MAX_LENGTH = 200;
export const TASK_DESCRIPTION_MAX_LENGTH = 5000;
export const TASK_ASSIGNEE_MAX_LENGTH = 254;
export const EMAIL_SUBJECT_MAX_LENGTH = 200;
export const EMAIL_BODY_MAX_LENGTH = 10000;
export const SLACK_CHANNEL_MAX_LENGTH = 80; // after the leading "#"
/** A rule's name (`automation_runner.RULE_NAME_MAX_LENGTH`): one line of visible text. */
export const RULE_NAME_MAX_LENGTH = 200;

/** The customer lifecycle stages `update_lifecycle.stage` accepts (platform_customers.LIFECYCLE_STAGES). */
export const LIFECYCLE_STAGES = ["lead", "trial", "active", "at_risk", "suspended", "churned"];

/**
 * Per action type, its config fields in the order the server checks them.
 * `kind`: "stage" | "tag" | "line" (single line) | "text" (multi-line) | "email" | "slackChannel".
 * `visible`: the value must have a character that renders. `max`: code points.
 */
export const ACTION_CONFIG_FIELDS = {
  update_lifecycle: [{ name: "stage", required: true, kind: "stage" }],
  tag_customer: [{ name: "tag", required: true, kind: "tag" }],
  create_task: [
    { name: "title", required: true, kind: "line", visible: true, max: TASK_TITLE_MAX_LENGTH },
    { name: "description", required: false, kind: "text", max: TASK_DESCRIPTION_MAX_LENGTH },
    { name: "assignee", required: false, kind: "line", max: TASK_ASSIGNEE_MAX_LENGTH },
  ],
  send_email: [
    { name: "subject", required: true, kind: "line", visible: true, max: EMAIL_SUBJECT_MAX_LENGTH },
    { name: "body", required: true, kind: "text", visible: true, max: EMAIL_BODY_MAX_LENGTH },
    { name: "to", required: false, kind: "email" },
  ],
  notify_slack: [{ name: "channel", required: true, kind: "slackChannel", max: SLACK_CHANNEL_MAX_LENGTH }],
};

// The server checks syntax only (email_validator, no deliverability): one "@", a dotted domain,
// no whitespace. This catches what a person mistypes; the server stays the authority.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SLACK_CHANNEL_SHAPE = /^#\S+$/u;

/** The fields of `type`'s config (`[]` for a type this module does not know). */
export const configFieldsOf = (type) => ACTION_CONFIG_FIELDS[type] || [];

const REQUIRED = Object.freeze({ code: "required" });
const INVALID = Object.freeze({ code: "invalid" });

/**
 * Why `value` breaks `field`'s rule, `null` when the server would accept it:
 *   { code: "required" }                 a required field is empty (absent, null, "", or, for a
 *                                        field that needs visible text, nothing that renders)
 *   { code: "tooLong", length, max }     longer than the limit (code points; a tag after its
 *                                        whitespace is collapsed, a channel after its "#")
 *   { code: "invalid" }                  any other break of the rule (shape, a character the field
 *                                        may not hold: hqCustomerRules.hasForbiddenCharacter)
 * An optional field that is absent, null or "" is fine: null means "not set" to the server too
 * (hq-customers-contract.md 4.3), and `configForSave` drops it.
 */
export function fieldProblem(field, value) {
  const absent = value === undefined || value === null || value === "";
  if (absent) return field.required ? REQUIRED : null;
  if (typeof value !== "string") return INVALID;
  const tooLong = (length) => ({ code: "tooLong", length, max: field.max });
  switch (field.kind) {
    case "stage": return LIFECYCLE_STAGES.includes(value) ? null : INVALID;
    case "tag": {
      const problem = tagProblem(value);
      if (problem === "empty" || problem === "invisible") return REQUIRED;
      if (problem === "tooLong") return { code: "tooLong", length: codePointLength(normalizeTag(value)), max: TAG_MAX_LENGTH };
      return problem ? INVALID : null;
    }
    case "email": return !EMAIL_SHAPE.test(value) || hasForbiddenCharacter(value) ? INVALID : null;
    case "slackChannel":
      if (!SLACK_CHANNEL_SHAPE.test(value) || hasForbiddenCharacter(value) || !hasVisibleCharacter(value.slice(1))) return INVALID;
      return codePointLength(value) - 1 > field.max ? tooLong(codePointLength(value) - 1) : null;
    case "line":
    case "text":
      if (codePointLength(value) > field.max) return tooLong(codePointLength(value));
      if (hasForbiddenCharacter(value, { multiline: field.kind === "text" })) return INVALID;
      return field.visible && !hasVisibleCharacter(value) ? REQUIRED : null;
    default: return INVALID;
  }
}
/**
 * The rule name as rule create and update check it (`automation_runner.rule_name_is_valid`,
 * answered `400 invalid_rule_name` otherwise): the same shape as a task title.
 */
const RULE_NAME_FIELD = Object.freeze({ name: "name", required: true, kind: "line", visible: true, max: RULE_NAME_MAX_LENGTH });
/** Why a rule name would be refused (`fieldProblem`'s shape), `null` when it is accepted. */
export const ruleNameProblem = (name) => fieldProblem(RULE_NAME_FIELD, name);

/** Whether `value` breaks `field`'s rule: `true` when the server would refuse it. */
export const fieldIsInvalid = (field, value) => fieldProblem(field, value) !== null;

/** The names of the fields of `config` the server would refuse, in its checking order. */
export function invalidConfigFields(type, config) {
  const values = config && typeof config === "object" && !Array.isArray(config) ? config : {};
  return configFieldsOf(type).filter((f) => fieldIsInvalid(f, values[f.name])).map((f) => f.name);
}

/**
 * The config as it is sent: the declared fields that hold a value. An optional field left
 * empty is not sent (absent means "none" to the server, and "" would be refused for `to`).
 * Keys the form does not declare are kept as they were stored.
 */
export function configForSave(type, config) {
  const values = config && typeof config === "object" && !Array.isArray(config) ? config : {};
  const declared = new Set(configFieldsOf(type).map((f) => f.name));
  return Object.fromEntries(Object.entries(values).filter(([k, v]) => !declared.has(k) || (v !== "" && v != null)));
}
